/** @jsx jsx */
/* istanbul ignore file */
import { jsx } from 'theme-ui';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import Bugsnag, { NotifiableError } from '@bugsnag/js';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  Select,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/core';
import Joi from 'joi';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import moment from 'moment';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiUserUnfollowFill } from 'react-icons/ri';
import { useLocation, useParams } from 'react-router';
import ConfirmationCodeInput from '../../app/components/ConfirmationCodeInput';
import { useAuthenticationStatus } from '../../app/hooks/useAuthenticationStatus';
import { AuthenticationStatus } from '../../app/models/appModel';
import { AppointmentType, CollaboratorBooking } from '../../firebase/firestore/documents/appointmentAvaiability';
import { RemoteConfigKey } from '../../firebase/remoteConfig';
import i18n from '../../locales/i18n';
import { useStoreActions, useStoreState } from '../../models/hooks';
import { GuestMeetParams } from '../../routes/constants';
import colors from '../../styles/colors';
import { errorFormat, FormattedError } from '../../utils/FormErrorUtils';
import { InviteCollaborators } from '../calendar/models/InviteCollaboratorsTypes';
import { getTranslatedValue } from '../../utils/localizedString';
import { CandidateInfoForGuest, ValidateURLCode, ValidateURLCodeExpiredError } from '../meet/MeetTokenResponse';
import { meetViewStyle } from '../meet/MeetVide.styles';
import { GuestInterviewRating } from './GuestInterviewRating';
import InterviewCandidateInformation from './InterviewCandidateInformation';
import useSingleCandidate from '../../app/hooks/useSingleCandidate';
import { useSeekerCandidates } from '../messaging/seeker/query/useSeekerCandidates';
import { AppointmentStatus } from '../../firebase/firestore/documents/appointmentStatus';

export type QueryParamsType = {
  appointmentAvailabilityId: string;
  bookingId: string;
  candidateId: string;
};

export type MeetGuestVerificationProps = {
  joinRoom: () => void;
  setSelectedAppointmentType: (appointmentType: AppointmentType) => void;
  isLoading: boolean;
};

export const MeetGuestVerification = ({
  joinRoom,
  isLoading,
  setSelectedAppointmentType,
}: MeetGuestVerificationProps): JSX.Element => {
  const { t } = useTranslation('meet');
  const toast = useToast();
  const { candidateId } = useParams<GuestMeetParams>();
  const searchParams = decodeURI(useLocation().search);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const queryParamsData = searchParams
    ? (JSON.parse(searchParams.replace('?', '')) as QueryParamsType)
    : { bookingId: '', appointmentAvailabilityId: '' };

  const authenticationStatus = useAuthenticationStatus();
  const singleCandidateData = useSingleCandidate(candidateId);

  const candidateData = candidateId?.split('_');

  useSeekerCandidates(candidateData?.[1], undefined, true);

  const loggedInUserEmail = useStoreState((s) => s.app.user?.email);
  const loggedInUserName = useStoreState((s) => s.app.user?.firstName);
  const userEmail: string = useStoreState((state) => state.meet.guestEmail);
  const gracePeriod: number = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.JOIN_VIDEO_GRACE_PERIOD_IN_MINUTE];
  });

  const signOut = useStoreActions((actions) => actions.auth.signOut);
  const { signInWithCustomToken } = useStoreActions((actions) => actions.auth);
  const { getCandidateDataOfMeet } = useStoreActions((actions) => actions.meet);
  const setUserEmail = useStoreActions((actions) => actions.meet.setGuestEmail);
  const { getInviteeEmail } = useStoreActions((actions) => actions.inviteCollaborators);
  const { verifyURLWithCodeV2, getInformationOfBooking } = useStoreActions((actions) => actions.meet);
  const { inviteCollaboratorsToInterview } = useStoreActions((actions) => actions.inviteCollaborators);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [formErrors, setFormErrors] = useState<FormattedError[]>([]);
  const [isLoadingBtn, setIsLoadingBtn] = useState<boolean>(false);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [userName, setUserName] = useState<string>('');

  const [bookingDetail, setBookingDetail] = useState<CollaboratorBooking | null>(null);
  const [isButtonEnabled, setIsButtonEnabled] = useState<boolean>(false);
  const [inviteeEmails, setInviteeEmails] = useState<string[]>([]);
  const [showCustomInput, setShowCustomInput] = useState<boolean>(false);
  const [showReviewRating, setShowReviewRating] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isloading, setIsLoading] = useState<boolean>(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [candidateInfo, setCandidateInfo] = useState<CandidateInfoForGuest | undefined>();
  const [canRateCandidate, setCanRateCandidate] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const isUserReady = !!loggedInUserEmail;

  useEffect(() => {
    if (isVerified) {
      sessionStorage.setItem('hasVerified', 'true');
    }
  }, [isVerified]);

  useEffect(() => {
    const hasVerified = sessionStorage.getItem('hasVerified') === 'true';
    if (authenticationStatus === AuthenticationStatus.AUTHENTICATED && !hasVerified) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      signOut();
    }

    if (authenticationStatus === AuthenticationStatus.NOT_AUTHENTICATED) {
      setUserName('');
      setVerificationCode('');
      setUserEmail('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticationStatus]);

  const fetchCandidateInfo = useCallback(async () => {
    if (!candidateData || candidateData.length < 2 || !candidateData[0] || !candidateData[1]) {
      console.warn('Invalid candidate data:', candidateData);
      return;
    }

    setIsLoading(true);
    try {
      const response = await getCandidateDataOfMeet({
        positionId: candidateData[0],
        seekerId: candidateData[1],
      });
      setCandidateInfo(response as any);
    } catch (err) {
      console.error('Error fetching candidate info:', err);
      Bugsnag.notify(err as NotifiableError);
    } finally {
      setIsLoading(false);
    }
  }, [candidateData, getCandidateDataOfMeet]);

  useEffect(() => {
    if (authenticationStatus === AuthenticationStatus.AUTHENTICATED) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetchCandidateInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticationStatus]);

  const handleInviteCollaboratorsToInterview = async () => {
    if (
      showCustomInput &&
      !_isEmpty(userEmail) &&
      !_isEmpty(userName) &&
      !_isNil(candidateId) &&
      !inviteeEmails.includes(userEmail)
    ) {
      try {
        const inviteData = {
          email: userEmail,
          fullName: userName,
          candidateId,
          appointmentAvailabilityId: queryParamsData.appointmentAvailabilityId,
          bookingId: queryParamsData.bookingId,
          locale: i18n.language,
        };

        await inviteCollaboratorsToInterview({ collaborators: inviteData });
      } catch (inviteErr) {
        console.error('Failed to send invite after onboarding:', inviteErr);
        Bugsnag.notify(inviteErr as NotifiableError);
        toast({
          title: t('guestMeet.failedToInvite', 'Failed to send invite email'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const getInformationOfInterviewBooking = useCallback(async () => {
    await getInformationOfBooking({
      availabilityId: queryParamsData.appointmentAvailabilityId,
      bookingId: queryParamsData.bookingId,
    })
      .then((responseData) => {
        setBookingDetail(responseData);
        setSelectedAppointmentType(responseData?.type as AppointmentType);
      })
      .finally(() => setIsLoadingBtn(false));
  }, [
    getInformationOfBooking,
    queryParamsData.appointmentAvailabilityId,
    queryParamsData.bookingId,
    setSelectedAppointmentType,
    setIsLoadingBtn,
  ]);

  useEffect(() => {
    if (authenticationStatus === AuthenticationStatus.AUTHENTICATED) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      getInformationOfInterviewBooking();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticationStatus]);

  const collaboratorInvite: InviteCollaborators = {
    fullName: userName,
    email: userEmail,
    appointmentAvailabilityId: queryParamsData.appointmentAvailabilityId,
    bookingId: queryParamsData.bookingId,
    locale: i18n.language,
  } as InviteCollaborators & { fullName: string };
  const schema = Joi.object()
    .options({ abortEarly: false })
    .keys({
      fullName: Joi.string().required(),
      email: Joi.string()
        .email({ tlds: { allow: false } })
        .messages({
          'string.empty': t('team:createTeamModal.validationMessage.empty'),
          'string.email': t('team:createTeamModal.validationMessage.validation'),
        }),
      appointmentAvailabilityId: Joi.string().required(),
      bookingId: Joi.string().required(),
      locale: Joi.string().required(),
    });

  const formValidation = (formData: InviteCollaborators) => {
    const { error } = schema.validate(formData);
    if (error) {
      setFormErrors(errorFormat(error.details));
      return false;
    }

    setFormErrors([]);
    return true;
  };

  // function to verify guest login token
  const handleGuestTokenVerification = async (codeValue: string) => {
    setIsLoadingBtn(true);
    try {
      const isValid = formValidation(collaboratorInvite);
      if (!isValid) {
        setIsLoadingBtn(false);
        return;
      }
      // Proceed with guest code verification
      const response = await verifyURLWithCodeV2({
        code: codeValue,
        bookingId: queryParamsData.bookingId,
        locale: moment.locale(i18n.language),
        timeOffset: moment.parseZone(new Date()).utcOffset(),
        email: userEmail,
        firstName: userName,
        candidateId: candidateId ?? '',
      });
      const { isValid: codeValid, customToken } = response as ValidateURLCode;
      const { message } = response as ValidateURLCodeExpiredError;

      if (codeValid && customToken) {
        try {
          await signInWithCustomToken(customToken);
          setIsVerified(true);
          await handleInviteCollaboratorsToInterview();
          joinRoom();
        } catch (authError) {
          console.error('Error signing in with custom token:', authError);
          Bugsnag.notify(authError as NotifiableError);
          toast({
            title: t('guestMeet.errorMessage', 'Login failed. Please try again.'),
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        } finally {
          setIsLoadingBtn(false);
        }
      } else {
        toast({
          title: message || t('guestMeet.invalidCode'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        setIsLoadingBtn(false);
      }
    } catch (err) {
      console.error('Error in handleGuestTokenVerification:', err);
      Bugsnag.notify(err as NotifiableError);
      setIsLoadingBtn(false);
    }
  };

  const isAbleToJoinVideoInterview = useCallback(
    (booking: CollaboratorBooking) => {
      return moment().isBetween(
        moment(booking.date).subtract(5, 'minutes'),
        moment(booking.date).add(booking.duration, 'minutes').add(gracePeriod, 'minute'),
      );
    },
    [gracePeriod],
  );

  const isAbleToRateCandidate = useCallback((booking: CollaboratorBooking | null) => {
    if (!booking) return false;
    return moment().isAfter(moment(booking.date).add(booking.duration, 'minutes'));
  }, []);

  const showJoinButton = useCallback(
    (booking: CollaboratorBooking) => {
      const now = moment();
      const interviewStart = moment(booking.date);
      const interviewEndWithGrace = moment(booking.date).add(booking.duration, 'minutes').add(gracePeriod, 'minutes');

      const beforeStart = now.isBefore(interviewStart);
      const duringInterview = now.isBetween(moment(booking.date).subtract(5, 'minutes'), interviewEndWithGrace);

      return beforeStart || duringInterview;
    },
    [gracePeriod],
  );

  useEffect(() => {
    if (!bookingDetail) {
      return;
    }
    const checkTime = () => {
      setIsButtonEnabled(isAbleToJoinVideoInterview(bookingDetail));
      setCanRateCandidate(isAbleToRateCandidate(bookingDetail));
    };

    // Check the time immediately when the component mounts
    checkTime();

    // Set an interval to check every minute
    intervalRef.current = setInterval(checkTime, 5 * 1000);

    // eslint-disable-next-line consistent-return
    return () => {
      // istanbul ignore next
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [bookingDetail, isAbleToJoinVideoInterview, isAbleToRateCandidate]);

  // istanbul ignore next
  const displayMessage = (bookingInfo: CollaboratorBooking) => {
    if (moment(bookingInfo.date).add(bookingInfo.duration, 'minutes').add(gracePeriod, 'minute').isBefore()) {
      return `${t('guestMeet.joinMessagePastInterview')}`;
    }
    if (moment().diff(bookingDetail?.date, 'days') !== 0) {
      return `${t('guestMeet.joinMessagePreInterviewOnAnotherDay', {
        day: `${moment(bookingDetail?.date).format('MMM')} ${moment(bookingDetail?.date).format('DD')}`,
        time: moment(bookingDetail?.date).format('LT'),
      })}`;
    }
    return `${t('guestMeet.joinMessagePreInterview', { time: moment(bookingDetail?.date).format('LT') })}`;
  };

  const validateEmail = (email: string) => {
    if (!email) return null;
    const emailSchema = Joi.string().email({ tlds: { allow: false } });
    const { error } = emailSchema.validate(email);
    return error;
  };

  const validationError = validateEmail(userEmail);
  // Fetch invitee emails when bookingId is available
  useEffect(() => {
    if (queryParamsData.bookingId) {
      const fetchEmails = async () => {
        try {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const emails = (await getInviteeEmail({ bookingId: queryParamsData.bookingId })) ?? /* istanbul ignore next */ [];
          /* istanbul ignore next */
          setInviteeEmails(emails);
        } catch (error) {
          /* istanbul ignore next */
          console.error('Failed to fetch invitee emails:', error);
          Bugsnag.notify(error as NotifiableError);
        }
      };

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetchEmails();
    }
  }, [getInviteeEmail, queryParamsData.bookingId]);

  return (
    <Box css={meetViewStyle}>
      <Box boxShadow="lg" mb={4}>
        {singleCandidateData?.appointment?.status === AppointmentStatus.cancelled && (
          <HStack bg="orange.50" color="red.600" px="5" py="4" borderRadius="2xl" gap="3" w="fit-content" marginBottom={2}>
            <Icon as={RiUserUnfollowFill} boxSize="6" color="red.600" />
            <Text fontWeight="bold">
              {t('guestMeet.interviewCancelled', {
                position: getTranslatedValue(singleCandidateData?.jobTitle, i18n.language),
                location: singleCandidateData?.businessName,
                time: singleCandidateData?.appointment?.fromDate
                  ? moment(singleCandidateData.appointment.fromDate).format('MMMM D, YYYY, h:mm A')
                  : '',
              })}
            </Text>
          </HStack>
        )}
        <Box className="meet-card-header">
          <Heading as="h3" size="md">
            {t('guestMeet.readyToJoin')}
          </Heading>
          {(authenticationStatus === AuthenticationStatus.NEW_USER ||
            authenticationStatus === AuthenticationStatus.NOT_AUTHENTICATED) && <Text>{t('guestMeet.enterInfoToJoin')}</Text>}
        </Box>
        <Box className="meet-card-body" spacing={4}>
          {authenticationStatus === AuthenticationStatus.NEW_USER ||
          authenticationStatus === AuthenticationStatus.NOT_AUTHENTICATED ? (
            <Fragment>
              <FormControl id="email" mb={3} isRequired>
                <FormLabel>{t('guestMeet.name')}</FormLabel>
                <Input
                  size="sm"
                  type="text"
                  value={userName}
                  placeholder={t('guestMeet.namePlaceholder')}
                  data-testid="NameInput"
                  onChange={(event) => setUserName(event.target.value.trimStart())}
                  required
                />
              </FormControl>
              <FormControl id="email" mb={5} isRequired>
                <FormLabel>{t('guestMeet.email')}</FormLabel>
                <Select
                  size="sm"
                  value={showCustomInput ? '_others_' : userEmail}
                  data-testid="EmailDropdown"
                  title=""
                  onChange={(event) => {
                    const selectedValue = event.target.value;
                    /* istanbul ignore next */
                    if (selectedValue === '_others_') {
                      setShowCustomInput(true);
                      setUserEmail('');
                    } else {
                      /* istanbul ignore next */
                      setShowCustomInput(false);
                      /* istanbul ignore next */
                      setUserEmail(selectedValue.trimStart());
                    }
                  }}
                  required={!showCustomInput}
                >
                  <option value="" disabled hidden>
                    {t('guestMeet.emailPlaceholder')}
                  </option>
                  {inviteeEmails.map(
                    /* istanbul ignore next */
                    (email) => (
                      <option key={email} value={email}>
                        {email}
                      </option>
                    ),
                  )}
                  <option value="_others_">{t('others')}</option>
                </Select>

                {showCustomInput && (
                  <VStack alignItems="start" mt={4}>
                    <Input
                      size="sm"
                      type="email"
                      placeholder={t('guestMeet.customEmailPlaceholder')}
                      onChange={(event) => setUserEmail(event.target.value.trimStart())}
                      required
                      data-testid="CustomEmailInput"
                      autoFocus
                    />
                  </VStack>
                )}

                {
                  /* istanbul ignore next */
                  validationError && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {t('guestMeet.invalidEmail')}
                    </Text>
                  )
                }
              </FormControl>
              <FormControl id="code" mb={5} isRequired>
                <FormLabel>{t('guestMeet.code')}</FormLabel>

                <ConfirmationCodeInput
                  verificationCode={verificationCode}
                  onChange={(e) => setVerificationCode(e)}
                  onComplete={() => {}}
                  isFromInterview
                />
              </FormControl>
              <Button
                mb={2}
                mr={2}
                size="sm"
                bg="blue.500"
                color={colors.white}
                _hover={{
                  bg: 'blue.600',
                }}
                data-testid="askToJoinBtn"
                isLoading={isLoadingBtn || isLoading}
                disabled={
                  isLoadingBtn ||
                  _isEmpty(userName) ||
                  _isEmpty(userEmail) ||
                  /* istanbul ignore next */
                  _isEmpty(verificationCode) ||
                  verificationCode.length < 6
                }
                onClick={/* istanbul ignore next */ () => handleGuestTokenVerification(verificationCode)}
              >
                {t('guestMeet.join')}
              </Button>
            </Fragment>
          ) : (
            <Fragment>
              {loggedInUserName && /* istanbul ignore next */ bookingDetail && (
                <Heading size="sm" mb={3}>
                  <Text>{t('guestMeet.joinAsLabel', { userName: loggedInUserName })}</Text>
                  {isButtonEnabled ? (
                    /* istanbul ignore next */ <Text>{t('guestMeet.joinMessage')}</Text>
                  ) : (
                    displayMessage(bookingDetail)
                  )}
                </Heading>
              )}
              {bookingDetail && bookingDetail?.type === AppointmentType.video && showJoinButton(bookingDetail) && (
                <Button
                  mb={2}
                  mr={2}
                  size="sm"
                  bg="blue.500"
                  color={colors.white}
                  _hover={{
                    bg: 'blue.600',
                  }}
                  data-testid="guestJoinBtn"
                  isLoading={isLoadingBtn || isLoading}
                  disabled={!isButtonEnabled || /* istanbul ignore next */ isLoadingBtn || /* istanbul ignore next */ isLoading}
                  onClick={/* istanbul ignore next */ () => joinRoom()}
                >
                  {t('guestMeet.join')}
                </Button>
              )}
              {bookingDetail && candidateInfo && !showJoinButton(bookingDetail) && canRateCandidate && (
                <Fragment>
                  {!isUserReady && (
                    <Flex alignItems="center" justifyContent="center" minH="200px">
                      <Spinner color="blue.500" />
                    </Flex>
                  )}
                  {isUserReady && (
                    <GuestInterviewRating
                      candidateInfo={candidateInfo}
                      showReviewRating={showReviewRating}
                      setShowReviewRating={setShowReviewRating}
                      quitRoom={/* istanbul ignore next */ () => {}}
                      candidateId={candidateId as string}
                      canRateCandidate={canRateCandidate}
                    />
                  )}
                </Fragment>
              )}
              <Button
                mb={2}
                size="sm"
                bg="blue.500"
                color={colors.white}
                _hover={{
                  bg: 'blue.600',
                }}
                onClick={onOpen}
              >
                {t('candidateInformation')}
              </Button>

              <Drawer isOpen={isOpen} placement="right" size="md" onClose={onClose}>
                <DrawerOverlay />
                <DrawerContent maxW={['100vw', 'md']} style={{ height: '100dvh', display: 'flex', flexDirection: 'column' }}>
                  <DrawerBody pl={2} pr={2} style={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
                    <InterviewCandidateInformation
                      candidateId={candidateId || /* istanbul ignore next */ ''}
                      setShowReviewRating={/* istanbul ignore next */ () => {}}
                      showReviewRating={false}
                      quitRoom={/* istanbul ignore next */ () => {}}
                      showCandidateAssessmentButton={(!_isNil(bookingDetail) && moment().isAfter(bookingDetail.date)) || false}
                      singleCandidateData={singleCandidateData}
                    />
                  </DrawerBody>

                  <DrawerFooter style={{ flex: '0 0 auto' }}>
                    <Button colorScheme="blue" onClick={onClose}>
                      {t('common:button.okay')}
                    </Button>
                  </DrawerFooter>
                </DrawerContent>
              </Drawer>
            </Fragment>
          )}
        </Box>
      </Box>
    </Box>
  );
};
